import type { Dispatch, FC, ReactNode } from "react"
import React, { useEffect, useState } from "react"
import type { ButtonProps, ComponentWithAs, IconProps } from "@chakra-ui/react"
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import type { FieldMetaProps } from "formik"
import { Formik, useFormikContext } from "formik"
import { ArrowForwardIcon, ChevronDownIcon, RepeatIcon } from "@chakra-ui/icons"

import { useAddress } from "web3/hooks/useAddress"
import CreateProposalImpactOverview from "createProposal/components/CreateProposalImpactOverview"
import ProposalThresholdWarningModal from "createProposal/components/ProposalThresholdWarningModal"
import CreateProposalAction from "createProposal/components/CreateProposalAction"
import CreateProposalSummary from "createProposal/components/CreateProposalSummary"
import { LayerPlus } from "ui/components/icons/font-awesome/LayerPlus"
import { useProposalThresholdRequirement } from "createProposal/hooks/useProposalThresholdRequirement"
import CreateProposalCTA from "createProposal/components/CreateProposalCTA"
import CreateProposalDetails from "createProposal/components/CreateProposalDetails"
import { useCreateProposal } from "createProposal/providers/CreateProposalProvider"
import { useCreateProposalForm } from "createProposal/hooks/useCreateProposalForm"
import { useValidationSchema } from "createProposal/hooks/useValidationSchema"
import type { DispatchAction } from "createProposal/types/dispatch"
import { DispatchActionMethod } from "createProposal/types/dispatch"
import type { CreateProposalFormValues } from "createProposal/types/form"
import {
  CreateProposalType,
  StepName,
  VotingOptionStrategy,
} from "createProposal/types/form"
import { useDevice } from "common/hooks/useDevice"
import EmptyAction from "createProposal/action/EmptyAction"
import { Text as TextIcon } from "ui/components/icons/font-awesome/solid/Text"
import type {
  ActionsSecurityCheck,
  EditTallyProposalQuery,
  ExecutableCallInput,
  Governor,
} from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import CreateProposalGovernorSelector from "createProposal/components/CreateProposalGovernanceSelector"
import CrossHairSimple from "ui/components/icons/CrossHairsSimple"
import { getProposalDescription } from "proposal/helpers/description"
import { getActions } from "createProposal/helpers/executablecalls"
import { PlusSquare } from "ui/components/icons/font-awesome/PlusSquare"
import CircleMark from "ui/components/icons/CircleMark"
import ShieldIcon from "ui/components/icons/ShieldIcon"
import SlashedShieldIcon from "ui/components/icons/SlashedShieldIcon"
import { isSecurityCheckSupportedInChain } from "web3/helpers/chains"
import { chainIdToChainReference } from "web3/helpers/transformers"
import { useRouter } from "common/hooks/useRouter"
import { isProduction } from "common/helpers/environment"
import type CustomAction from "createProposal/action/CustomAction"
import GenericModal from "ui/components/GenericModal"
import { Play } from "ui/components/icons/font-awesome/Play"
import { FileLines } from "ui/components/icons/font-awesome/FileLines"
import { spacing } from "theme/foundations/spacing"

const CreateProposalForm: FC<{
  isWhiteLabel: boolean
  isEditMode: boolean
  governanceSlug: string
  tallyProposal?: EditTallyProposalQuery["proposal"]
  organizationId: string
  originalProposalId?: string
}> = ({
  isWhiteLabel,
  governanceSlug,
  tallyProposal,
  organizationId,
  originalProposalId,
  isEditMode,
}) => {
  const { governance, governanceId, governanceIds, tokenBalances } =
    useCreateProposal()
  const { state, dispatch } = useCreateProposalForm({
    firstStep: StepName.DETAILS,
  })
  const [initialValues, setInitialValues] = useState<
    CreateProposalFormValues | undefined
  >(undefined)

  const [actionIndex, setActionIndex] = useState(0)

  const { isOpen, onOpen, onClose } = useDisclosure()

  const { delegatedVotingPower, isProposalThresholdMet } =
    useProposalThresholdRequirement(governance)

  const { currentStep } = state

  const address = useAddress()
  const validationSchema = useValidationSchema()

  const [proposalSecurityCheck, setProposalSecurityCheck] =
    useState<ActionsSecurityCheck | null>(null)

  const [securityChecksRan, setSecurityChecksRan] = useState(false)
  const [displaySimulationModal, setDisplaySimulationModal] = useState(false)
  const [displayArbitrumComplianceNote, setDisplayArbitrumComplianceNote] =
    useState(false)
  const [acceptedComplianceNote, setAcceptedComplianceNote] = useState(false)
  const [triggerRunSecurityChecks, setTriggerRunSecurityChecks] = useState(0)
  const [editionMode, setEditionMode] = useState<"edit" | "preview">("edit")

  useEffect(() => {
    if (isEditMode && tallyProposal) {
      // Init values to edit a draft proposal
      const getInitialValues = async () => {
        const decodedActions = await getActions(
          governance.chainId,
          governanceId,
          tallyProposal.executableCalls as ExecutableCallInput[],
          tokenBalances,
        )

        const description = getProposalDescription(
          tallyProposal.metadata.description,
        )

        const actions =
          decodedActions && decodedActions.length > 0
            ? decodedActions
            : [new EmptyAction({ targetAddress: address })]

        return {
          createProposalType: CreateProposalType.GOVERNOR_PROPOSAL,
          details: {
            title: tallyProposal.metadata.title,
            description: description,
            snapshotURL: tallyProposal.metadata.snapshotURL,
            discourseURL: tallyProposal.metadata.discourseURL,
          },
          votingOption: {
            strategy: VotingOptionStrategy.DEFAULT,
            currentChoice: "",
          },
          actions,
        }
      }

      setInitialValues(undefined)
      setTimeout(() => {
        getInitialValues().then((initialValues) => {
          // Force re-render to update formik initialValues
          setInitialValues(initialValues)
        })
      }, 300)

      return
    }

    // If isEditMode and there is no tallyProposal nor tokenBalances yet, skip the form initialization
    if (isEditMode) {
      return
    }

    // Init values to create a new proposal
    const defaultInitialValues = {
      createProposalType: CreateProposalType.GOVERNOR_PROPOSAL,
      details: {
        title: "",
        description: "",
        snapshotURL: "",
        discourseURL: "",
      },
      votingOption: {
        strategy: VotingOptionStrategy.DEFAULT,
        currentChoice: "",
      },
      actions: [new EmptyAction({ targetAddress: address })],
    }
    setInitialValues(defaultInitialValues)
  }, [
    address,
    isEditMode,
    governance.chainId,
    tallyProposal,
    tokenBalances,
    governanceId,
  ])

  if (!initialValues) {
    return <LoadingState />
  }

  const isMultiGov = governanceIds ? governanceIds.length > 1 : false

  const handleRunSecurityChecks = () => {
    setTriggerRunSecurityChecks((prev) => prev + 1) // using a trigger this way to not: 1. refactor this component since it doesn't have access to form values. 2. Risk triggering security checks twice

    dispatch({
      method: DispatchActionMethod.TO_STEP,
      payload: { to: StepName.IMPACT },
    })

    setSecurityChecksRan(true)

    setDisplaySimulationModal(false)
  }

  return (
    <>
      <Formik
        validateOnMount
        initialValues={initialValues}
        validationSchema={validationSchema}
        // our form has two submit actions, submitting in `CreateProposalSummary` simplifies this
        onSubmit={() => console.log("no-op")}
      >
        {({ handleSubmit, getFieldMeta, setFieldTouched }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={spacing.containerGap}>
              <Stack
                direction="row"
                sx={{
                  borderBottomColor: "gray.200 !important",
                }}
              >
                <EditionMode
                  editionMode={editionMode}
                  onClick={(newEditionMode) => {
                    if (newEditionMode === editionMode) {
                      return
                    }

                    setEditionMode(newEditionMode)
                  }}
                />

                <Spacer />
                <Flex align="end">
                  <CreateProposalCTA
                    acceptedComplianceNote={acceptedComplianceNote}
                    displayArbitrumComplianceNote={
                      displayArbitrumComplianceNote
                    }
                    displaySimulationModal={displaySimulationModal}
                    governance={governance}
                    governanceSlug={governanceSlug}
                    handleRunSecurityChecks={handleRunSecurityChecks}
                    isProposalThresholdMet={isProposalThresholdMet ?? true}
                    isWhiteLabel={isWhiteLabel}
                    originalProposalId={originalProposalId ?? undefined}
                    securityChecksRan={securityChecksRan}
                    setAcceptedComplianceNote={setAcceptedComplianceNote}
                    setDisplayArbitrumComplianceNote={
                      setDisplayArbitrumComplianceNote
                    }
                    setDisplaySimulationModal={setDisplaySimulationModal}
                    tallyProposal={tallyProposal}
                    onPublishClick={() => {
                      if (!isProposalThresholdMet) {
                        onOpen()
                      }
                    }}
                  />
                </Flex>
              </Stack>
              <Divider />

              {/* Edit layout */}
              <Stack
                direction={{ base: "column", md: "row" }}
                spacing={spacing.containerGap}
                style={{
                  display: editionMode === "edit" ? "flex" : "none",
                }}
              >
                <NavigationMenu
                  actionIndex={actionIndex}
                  currentStep={currentStep}
                  dispatch={dispatch}
                  getFieldMeta={getFieldMeta}
                  governance={governance}
                  isMultiGov={isMultiGov}
                  securityChecksRan={securityChecksRan}
                  setActionIndex={setActionIndex}
                  setFieldTouched={setFieldTouched}
                />

                <Stack width="100%">
                  {isMultiGov ? (
                    <CreateProposalGovernorSelector
                      isEditMode={isEditMode}
                      organizationId={organizationId}
                      slug={governanceSlug}
                      state={state}
                      targetGovernanceId={governance?.id}
                    />
                  ) : null}
                  <CreateProposalDetails state={state} />
                  <CreateProposalAction
                    chainId={governance?.chainId}
                    governance={governance}
                    index={actionIndex}
                    setActionIndex={setActionIndex}
                    state={state}
                    timelockId={governance?.timelockId}
                  />

                  <CreateProposalImpactOverview
                    currentStep={currentStep}
                    governance={governance}
                    proposalSecurityCheck={proposalSecurityCheck}
                    setDisplaySimulationModal={setDisplaySimulationModal}
                    setProposalSecurityCheck={setProposalSecurityCheck}
                    setSecurityChecksRan={setSecurityChecksRan}
                    triggerRunSecurityChecks={triggerRunSecurityChecks}
                  />
                </Stack>
              </Stack>

              {/* Preview layout */}
              <Stack
                style={{
                  display: editionMode === "preview" ? "flex" : "none",
                }}
              >
                <CreateProposalSummary
                  governance={governance}
                  isWhiteLabel={isWhiteLabel}
                />
              </Stack>
            </Stack>
          </form>
        )}
      </Formik>
      <ProposalThresholdWarningModal
        decimals={governance.token.decimals}
        delegatedVotingPower={delegatedVotingPower}
        governanceSlug={governanceSlug}
        isOpen={isOpen}
        proposalThreshold={governance.parameters.proposalThreshold}
        onClose={onClose}
      />
    </>
  )
}

type EditionModeProps = {
  editionMode: string
  onClick: (newEditionModel: "edit" | "preview") => void
}
const EditionMode: FC<EditionModeProps> = ({ editionMode, onClick }) => {
  return (
    <Stack direction="row">
      <Button
        bgColor={editionMode === "edit" ? "gray.100" : undefined}
        color={editionMode === "edit" ? "gray.700" : "gray.500"}
        leftIcon={
          <Icon
            as={FileLines}
            color={editionMode === "edit" ? "gray.700" : "gray.500"}
          />
        }
        variant="tertiary"
        onClick={() => onClick("edit")}
      >
        Edit
      </Button>
      <Button
        bgColor={editionMode === "preview" ? "gray.100" : undefined}
        color={editionMode === "preview" ? "gray.700" : "gray.500"}
        leftIcon={
          <Icon
            as={Play}
            color={editionMode === "preview" ? "gray.700" : "gray.500"}
          />
        }
        variant="tertiary"
        onClick={() => onClick("preview")}
      >
        Preview
      </Button>
    </Stack>
  )
}

type NavigationOptionProps = {
  children: ReactNode
  icon: ComponentWithAs<"svg", IconProps>
  isError?: boolean
  onClick?: () => void
}
const NavigationOption: FC<NavigationOptionProps & ButtonProps> = ({
  children,
  icon,
  onClick,
  isError = false,
  ...buttonProps
}) => {
  return (
    <Box as="li" listStyleType="none" role="none" width="100%">
      <Button
        bg="white"
        h="full"
        px={5}
        py={4}
        rounded="md"
        w="full"
        onClick={onClick}
        {...buttonProps}
      >
        <Stack align="center" direction="row" justify="flex-start" w="full">
          <Icon as={icon} fill="gray.700" h={4} w={4} />
          <Text
            color="neutral.700"
            fontSize="16px"
            fontWeight="medium"
            lineHeight="24px"
            textAlign="left"
            width="100%"
          >
            {children}
          </Text>

          {isError ? (
            <Icon as={CircleMark} color="red.600" h={4} w={4} />
          ) : null}
        </Stack>
      </Button>
    </Box>
  )
}

type NavigationMenuProps = {
  currentStep: StepName
  dispatch: Dispatch<DispatchAction>
  actionIndex: number
  setActionIndex: any
  getFieldMeta: (name: string) => FieldMetaProps<any>
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined,
  ) => void
  governance?: Pick<Governor, "name" | "chainId">
  isMultiGov?: boolean
  securityChecksRan: boolean
}
const NavigationMenu: FC<NavigationMenuProps> = ({
  currentStep,
  dispatch,
  setActionIndex,
  actionIndex,
  getFieldMeta,
  setFieldTouched,
  governance,
  isMultiGov = false,
  securityChecksRan,
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()
  const address = useAddress()
  const router = useRouter()
  const { formState } = useCreateProposal()

  const [targetRouterUrl, setTargetRouterUrl] = useState<string | undefined>(
    undefined,
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoseChangesConfirm, setIsLoseChangesConfirm] = useState(false)

  const { values, errors, dirty, setFieldValue, validateField } =
    useFormikContext<CreateProposalFormValues>()

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (dirty && formState !== "success" && !isLoseChangesConfirm) {
        setIsModalOpen(true)
        setTargetRouterUrl(url)

        router.events.emit("routeChangeError")
        if (isProduction) {
          throw new Error("Navigation cancelled due to unsaved changes")
        }
      }
    }

    window.onbeforeunload = dirty && formState !== "success" ? () => true : null

    router.events.on("routeChangeStart", handleRouteChange)

    return () => {
      window.onbeforeunload = null
      router.events.off("routeChangeStart", handleRouteChange)
    }
  }, [dirty, formState, isLoseChangesConfirm, router])

  useEffect(() => {
    if (!isLoseChangesConfirm) return

    if (isLoseChangesConfirm && targetRouterUrl) {
      router.push(targetRouterUrl)
    }
  }, [isLoseChangesConfirm, router, targetRouterUrl])

  const getLabelAndIcon = (currentStep: StepName) => {
    if (currentStep === StepName.ACTIONS) {
      return {
        icon: LayerPlus,
        label: "Actions",
      }
    }

    if (currentStep === StepName.IMPACT) {
      return {
        icon: securityChecksRan ? ShieldIcon : SlashedShieldIcon,
        label: "Impact Overview",
      }
    }

    return {
      icon: TextIcon,
      label: "Proposal text",
    }
  }

  // don't add action if current action is empty, unless you are in the proposal details step and there are no non-empty actions
  const isAddActionDisabled =
    values?.actions?.[actionIndex]?.recipe === ExecutableCallType.Empty &&
    currentStep === StepName.ACTIONS &&
    values?.actions?.length > 0

  const { label, icon } = getLabelAndIcon(currentStep)

  const isProposalError =
    (Boolean(getFieldMeta("details.title").error) &&
      Boolean(getFieldMeta("details.title").touched)) ||
    (Boolean(getFieldMeta("details.description").error) &&
      Boolean(getFieldMeta("details.description").touched))

  const handleAddActionClick = () => {
    if (values.actions) {
      // don't increment actionIndex if the current action is empty and you are in the details step
      if (
        values.actions[actionIndex]?.recipe === ExecutableCallType.Empty &&
        currentStep === StepName.DETAILS
      ) {
        dispatch({
          method: DispatchActionMethod.TO_STEP,
          payload: { to: StepName.ACTIONS },
        })
        setFieldTouched("details.title", true, true)
        setFieldTouched("details.description", true, true)

        return
      }

      // if the last action in the array is empty, don't add any actions, just move the actionIndex to that last action
      if (
        values.actions[values.actions.length - 1]?.recipe ===
        ExecutableCallType.Empty
      ) {
        dispatch({
          method: DispatchActionMethod.TO_STEP,
          payload: { to: StepName.ACTIONS },
        })
        setFieldTouched("details.title", true, true)
        setFieldTouched("details.description", true, true)
        setActionIndex(values.actions.length - 1)

        return
      }

      setFieldValue("actions", [
        ...values?.actions,
        new EmptyAction({ targetAddress: address }),
      ])
      dispatch({
        method: DispatchActionMethod.TO_STEP,
        payload: { to: StepName.ACTIONS },
      })
      setFieldTouched("details.title", true, true)
      setFieldTouched("details.description", true, true)

      if (values.actions[actionIndex]) {
        validateField(`actions.${actionIndex}`)
        setFieldTouched(`actions.${actionIndex}.targetAddress`, true, true)
      }

      setActionIndex(values.actions.length)
    }
  }

  const handleProposalMenuClick = () => {
    if (values?.actions && values?.actions.length > 1) {
      values.actions.map((_, idx) => {
        validateField(`actions.${idx}`)
        setFieldTouched(`actions.${idx}.targetAddress`, true, true)
      })
    }
  }

  const handleActionMenuClick = (index: number) => {
    setActionIndex(index)
    setFieldTouched("details.title", true, true)
    setFieldTouched("details.description", true, true)
    if (values?.actions) {
      values.actions.map((_, idx) => {
        validateField(`actions.${idx}`)
        setFieldTouched(`actions.${idx}.targetAddress`, true, true)
      })
    }
  }

  const handlePreviewMenuClick = () => {
    setFieldTouched("details.title", true, true)
    setFieldTouched("details.description", true, true)
    if (values?.actions?.length && values?.actions?.length > 0) {
      values.actions.map((_, idx) => {
        validateField(`actions.${idx}`)
        setFieldTouched(`actions.${idx}.targetAddress`, true, true)
      })
    }
  }

  const handleImpactMenuClick = () => {
    setFieldTouched("details.title", true, true)
    setFieldTouched("details.description", true, true)
    if (values?.actions?.length && values?.actions?.length > 1) {
      values.actions.map((_, idx) => {
        validateField(`actions.${idx}`)
        setFieldTouched(`actions.${idx}.targetAddress`, true, true)
      })
    }
  }

  const getRecipeIcon = (recipe: ExecutableCallType) => {
    switch (recipe) {
      case ExecutableCallType.Custom:
        return LayerPlus

      case ExecutableCallType.Erc20transfer:
      case ExecutableCallType.Nativetransfer:
      case ExecutableCallType.Erc20transferarbitrum:
        return ArrowForwardIcon

      case ExecutableCallType.Swap:
        return RepeatIcon

      default:
        return LayerPlus
    }
  }

  const getRecipeName = (recipe: ExecutableCallType) => {
    switch (recipe) {
      case ExecutableCallType.Custom:
        return "Custom"

      case ExecutableCallType.Erc20transfer:
      case ExecutableCallType.Nativetransfer:
      case ExecutableCallType.Erc20transferarbitrum:
        return "Transfer"

      case ExecutableCallType.Swap:
        return "Swap"

      default:
        return "Action"
    }
  }

  const getActionSignature = (action: any) => {
    const { recipe } = action

    switch (recipe) {
      case ExecutableCallType.Custom:
        const customAction = action as CustomAction

        const signature = customAction.selectedAbiMethod?.name
          ? `${customAction.selectedAbiMethod?.name}(..)`
          : undefined

        return signature

      case ExecutableCallType.Erc20transfer:
      case ExecutableCallType.Nativetransfer:
      case ExecutableCallType.Erc20transferarbitrum:
        return "transfer(..)"

      case ExecutableCallType.Swap:
        return "swap(..)"

      default:
        return undefined
    }
  }

  const isSecurityCheckSupported =
    governance && governance?.chainId
      ? isSecurityCheckSupportedInChain(
          chainIdToChainReference(governance.chainId),
        )
      : false

  return (
    <>
      <Stack as="nav" display={onLargeDevice} minWidth="300px" spacing={3}>
        {isMultiGov ? (
          <NavigationOption
            borderColor={
              currentStep === StepName.SELECTOR_GOVERNOR
                ? "gray.700"
                : "neutral.200"
            }
            borderStyle="solid"
            borderWidth="1px"
            icon={CrossHairSimple}
            onClick={() => {
              dispatch({
                method: DispatchActionMethod.TO_STEP,
                payload: { to: StepName.SELECTOR_GOVERNOR },
              })
            }}
          >
            Governor{" "}
            <Text as="span" color="gray.600" fontSize="sm" ml={2}>
              {governance?.name}
            </Text>
          </NavigationOption>
        ) : null}
        <NavigationOption
          borderColor={
            currentStep === StepName.DETAILS ? "gray.700" : "neutral.200"
          }
          borderStyle="solid"
          borderWidth="1px"
          icon={TextIcon}
          isError={isProposalError}
          onClick={() => {
            handleProposalMenuClick()
            dispatch({
              method: DispatchActionMethod.TO_STEP,
              payload: { to: StepName.DETAILS },
            })
          }}
        >
          Proposal text
        </NavigationOption>

        {values.actions?.map((action, idx) => {
          const hasError =
            Boolean(errors?.actions?.[idx]) &&
            Boolean(
              getFieldMeta(`actions.${idx}`).touched &&
                Boolean(getFieldMeta(`actions.${idx}`).error),
            )

          if (action.recipe === ExecutableCallType.Empty) return null

          if (!action) return null

          const icon = getRecipeIcon(action.recipe as ExecutableCallType)

          return (
            <NavigationOption
              key={idx}
              borderColor={
                currentStep === StepName.ACTIONS && actionIndex === idx
                  ? "gray.700"
                  : "neutral.200"
              }
              borderStyle="solid"
              borderWidth="1px"
              height="60px"
              icon={icon}
              isError={hasError}
              onClick={() => {
                handleActionMenuClick(idx)
                dispatch({
                  method: DispatchActionMethod.TO_STEP,
                  payload: { to: StepName.ACTIONS },
                })
              }}
            >
              <Stack
                direction="column"
                height="50px"
                justify="center"
                spacing={0}
              >
                <Text as="span">
                  {getRecipeName(action.recipe as ExecutableCallType)}
                </Text>
                <Text
                  as="span"
                  color="gray.500"
                  fontSize="sm"
                  fontWeight="regular"
                  mt="-4px"
                >
                  {getActionSignature(action)}
                </Text>
              </Stack>
            </NavigationOption>
          )
        })}

        <Button
          isDisabled={isAddActionDisabled}
          px={5}
          py={7}
          rounded="md"
          variant="primary"
          w="full"
          onClick={() => handleAddActionClick()}
        >
          <HStack>
            <Icon as={PlusSquare} color="white" fill="white" h={4} w={4} />
            <Text
              fontSize="16px"
              fontWeight="medium"
              lineHeight="24px"
              textAlign="left"
              width="100%"
            >
              Add Action
            </Text>
          </HStack>
        </Button>
        {isSecurityCheckSupported ? (
          <NavigationOption
            borderColor={
              currentStep === StepName.IMPACT ? "gray.700" : "neutral.200"
            }
            borderStyle="solid"
            borderWidth="1px"
            icon={securityChecksRan ? ShieldIcon : SlashedShieldIcon}
            onClick={() => {
              handleImpactMenuClick()
              dispatch({
                method: DispatchActionMethod.TO_STEP,
                payload: { to: StepName.IMPACT },
              })
            }}
          >
            Impact overview
          </NavigationOption>
        ) : null}
      </Stack>

      <Box display={onLittleDevice}>
        <Menu>
          <MenuButton
            _active={{ bgColor: "white" }}
            _empty={{ bgColor: "white" }}
            _focus={{ bgColor: "white" }}
            as={Button}
            bgColor="white"
            borderWidth="1px"
            height="70px"
            m={0}
            rightIcon={<ChevronDownIcon />}
            width="100%"
          >
            <NavigationOption icon={icon}>{label}</NavigationOption>
          </MenuButton>
          <MenuList mt={-2} mx={4} rootProps={{ width: "100%" }} zIndex={500}>
            {isMultiGov ? (
              <MenuItem
                bgColor="white"
                height="70px"
                width="100%"
                onClick={() =>
                  dispatch({
                    method: DispatchActionMethod.TO_STEP,
                    payload: { to: StepName.SELECTOR_GOVERNOR },
                  })
                }
              >
                <NavigationOption icon={CrossHairSimple} width="100%">
                  Governor: {governance?.name}
                </NavigationOption>
              </MenuItem>
            ) : null}
            <MenuItem
              bgColor="white"
              height="70px"
              width="100%"
              onClick={() =>
                dispatch({
                  method: DispatchActionMethod.TO_STEP,
                  payload: { to: StepName.DETAILS },
                })
              }
            >
              <NavigationOption
                icon={TextIcon}
                isError={isProposalError}
                width="100%"
              >
                Proposal
              </NavigationOption>
            </MenuItem>

            {values.actions?.map((action, idx) => {
              const hasError =
                Boolean(errors?.actions?.[idx]) &&
                Boolean(
                  getFieldMeta(`actions.${idx}`).touched &&
                    Boolean(getFieldMeta(`actions.${idx}`).error),
                )

              if (action.recipe === ExecutableCallType.Empty) return null

              if (!action) return null

              const icon = getRecipeIcon(action.recipe as ExecutableCallType)

              return (
                <MenuItem
                  key={idx}
                  height="70px"
                  width="100%"
                  onClick={() =>
                    dispatch({
                      method: DispatchActionMethod.TO_STEP,
                      payload: { to: StepName.ACTIONS },
                    })
                  }
                >
                  <NavigationOption
                    borderColor={
                      currentStep === StepName.ACTIONS && actionIndex === idx
                        ? "gray.700"
                        : "neutral.200"
                    }
                    icon={icon}
                    isError={hasError}
                    onClick={() => {
                      handleActionMenuClick(idx)
                      dispatch({
                        method: DispatchActionMethod.TO_STEP,
                        payload: { to: StepName.ACTIONS },
                      })
                    }}
                  >
                    <Stack
                      direction="column"
                      height="50px"
                      justify="center"
                      spacing={0}
                    >
                      <Text as="span">
                        {getRecipeName(action.recipe as ExecutableCallType)}
                      </Text>
                      <Text
                        as="span"
                        color="gray.500"
                        fontSize="sm"
                        fontWeight="regular"
                        mt="-4px"
                      >
                        {getActionSignature(action)}
                      </Text>
                    </Stack>
                  </NavigationOption>
                </MenuItem>
              )
            })}

            {isSecurityCheckSupported ? (
              <MenuItem
                height="70px"
                width="100%"
                onClick={() => {
                  handlePreviewMenuClick()
                  dispatch({
                    method: DispatchActionMethod.TO_STEP,
                    payload: { to: StepName.IMPACT },
                  })
                }}
              >
                <NavigationOption
                  icon={securityChecksRan ? ShieldIcon : SlashedShieldIcon}
                  width="100%"
                >
                  Impact Overview
                </NavigationOption>
              </MenuItem>
            ) : null}
            <MenuItem>
              <Button
                isDisabled={isAddActionDisabled}
                px={5}
                py={7}
                rounded="md"
                variant="primary"
                w="full"
                onClick={() => handleAddActionClick()}
              >
                <HStack>
                  <Icon
                    as={PlusSquare}
                    color="white"
                    fill="white"
                    h={4}
                    w={4}
                  />
                  <Text
                    color="white"
                    fontSize="16px"
                    fontWeight="medium"
                    lineHeight="24px"
                    textAlign="left"
                    width="100%"
                  >
                    Add Action
                  </Text>
                </HStack>
              </Button>
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>

      <GenericModal
        footer={
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={4}
            width="full"
          >
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={() => {
                setIsModalOpen(false)
              }}
            >
              Stay on page
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setIsModalOpen(false)
                setIsLoseChangesConfirm(true)
              }}
            >
              Leave without saving
            </Button>
          </Stack>
        }
        isOpen={isModalOpen}
        maxWidth="450px"
        title="Unsaved changes"
        onClose={() => {
          setIsModalOpen(false)
        }}
      >
        <Text>
          There are unsaved changes on this page. Are you sure you want to leave
          without saving them?
        </Text>
      </GenericModal>
    </>
  )
}

const LoadingState = () => {
  const { onLargeDevice, onLittleDevice } = useDevice()

  return (
    <>
      <Flex direction="column" display={onLargeDevice} justify="center">
        <Flex width="100%">
          <Stack
            align="flex-end"
            direction="row"
            justify="flex-end"
            mb={4}
            width="100%"
          >
            <Skeleton h={10} w={40} />
            <Skeleton h={10} w={40} />
            <Skeleton h={10} w={40} />
          </Stack>
        </Flex>
        <Stack direction="row" mx={0} px={0} width="full">
          <Stack direction="column" width="35%">
            <Skeleton h={16} w="full" />
            <Skeleton h={16} w="full" />
            <Skeleton h={16} w="full" />
          </Stack>
          <Skeleton h={80} w="full" />
        </Stack>
      </Flex>

      <Stack direction="column" display={onLittleDevice} spacing={2}>
        <Skeleton h={16} w="full" />
        <Skeleton h={80} w="full" />
      </Stack>
    </>
  )
}

export default CreateProposalForm
